import React, { useCallback, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '../../../basic/Button';
import Typography from '../../../basic/Typography';
import { useAuth } from '../context/AuthContext';
import { AuthLayout } from './AuthLayout';
import styles from './AuthForm.module.css';

function useRedirectParam(): string {
  const { search } = useLocation();
  return useMemo(() => {
    const raw = new URLSearchParams(search).get('redirect');
    if (!raw) return '/studio';
    // Allow relative paths to /auth or /studio
    if (raw.startsWith('/auth') || raw.startsWith('/studio')) return raw;
    return '/studio';
  }, [search]);
}

export function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const redirectTo = useRedirectParam();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        await auth.signup({ name, email, password });
        navigate(redirectTo);
      } catch {
        // AuthContext already captures and exposes the error.
      }
    },
    [auth, email, name, navigate, password, redirectTo],
  );

  if (auth.status === 'authenticated') return <Navigate to={redirectTo} replace />;

  return (
    <AuthLayout title="Create account">
      <form className={styles.form} onSubmit={onSubmit}>
        {auth.error && (
          <div className={styles.error} role="alert">
            <Typography variant="body2">{auth.error}</Typography>
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="name">
            <Typography variant="body2" component="span">
              Name
            </Typography>
          </label>
          <input
            id="name"
            className={styles.input}
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Optional"
          />
          <Typography variant="caption" component="div" className={styles.hint}>
            If you leave this blank, we’ll use your email.
          </Typography>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="email">
            <Typography variant="body2" component="span">
              Email
            </Typography>
          </label>
          <input
            id="email"
            className={styles.input}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="password">
            <Typography variant="body2" component="span">
              Password
            </Typography>
          </label>
          <input
            id="password"
            className={styles.input}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
          <Typography variant="caption" component="div" className={styles.hint}>
            Minimum 8 characters.
          </Typography>
        </div>

        <div className={styles.actions}>
          <Button size="large" disabled={auth.isLoading} className={styles.submitButton}>
            {auth.isLoading ? 'Creating…' : 'Create account'}
          </Button>

          <div className={styles.linksRow}>
            <Typography variant="body2" component="span" color="secondary">
              Already have an account?
            </Typography>
            <Link to={`/auth/login${location.search}`}>
              <Typography variant="body2" component="span" color="primary">
                Log in
              </Typography>
            </Link>
          </div>
        </div>
      </form>
    </AuthLayout>
  );
}
